import React, { useEffect, useRef, useState } from "react";

function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
}

const reveal = (inView, delay = 0) => ({
  opacity: inView ? 1 : 0,
  transform: inView ? "translateY(0)" : "translateY(14px)",
  transition: `opacity 480ms ease ${delay}ms, transform 480ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
});

const muted = "rgba(26,26,25,0.55)";
const body = "rgba(26,26,25,0.78)";

function InfoBlock({ label, children, inView, delay, testid }) {
  return (
    <div style={reveal(inView, delay)} data-testid={testid}>
      <p
        className="tracking-[0.32em] uppercase"
        style={{ fontSize: "10.5px", color: muted }}
      >
        {label}
      </p>
      <div className="mt-3">{children}</div>
    </div>
  );
}

export default function Contact() {
  const [ref, inView] = useInView();

  return (
    <main
      ref={ref}
      className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 pt-16 sm:pt-20 pb-24 sm:pb-32"
      data-testid="contact-page"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-16 gap-x-16 lg:gap-x-24">
        {/* Left: heading & intro */}
        <div className="lg:col-span-7">
          <p
            className="tracking-[0.32em] uppercase text-[11px]"
            style={{ color: muted, ...reveal(inView, 0) }}
            data-testid="contact-eyebrow"
          >
            Contact
          </p>

          <h1
            className="font-display mt-8 sm:mt-10 leading-[1.05]"
            style={{
              fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)",
              letterSpacing: "-0.005em",
              fontWeight: 400,
              ...reveal(inView, 80),
            }}
            data-testid="contact-heading"
          >
            We'd Love to <span className="italic">Hear From You</span>
          </h1>

          <p
            className="mt-12 sm:mt-14 text-base sm:text-[17px] leading-[1.85] max-w-[560px]"
            style={{ color: body, ...reveal(inView, 200) }}
            data-testid="contact-intro"
          >
            Whether you have a question about sizing, an order, or a potential
            collaboration, we'd be delighted to hear from you.
          </p>
        </div>

        {/* Right: contact details */}
        <aside
          className="lg:col-span-5 lg:pt-2 space-y-12"
          data-testid="contact-details"
        >
          <InfoBlock label="Email" inView={inView} delay={280} testid="contact-block-email">
            <a
              href="mailto:support@tamorebytamanna.com"
              className="font-display text-2xl sm:text-3xl leading-snug hover:opacity-70 transition-opacity inline-block"
              style={{ letterSpacing: "-0.005em", color: "#1A1A19" }}
              data-testid="contact-email-link"
            >
              support@tamorebytamanna.com
            </a>
          </InfoBlock>

          <InfoBlock label="Instagram" inView={inView} delay={340} testid="contact-block-instagram">
            <a
              href="https://instagram.com/tamorebytamanna"
              target="_blank"
              rel="noopener noreferrer"
              className="font-display italic text-xl hover:opacity-70 transition-opacity inline-block"
              style={{ color: "#1A1A19" }}
              data-testid="contact-instagram-link"
            >
              @tamorebytamanna
            </a>
          </InfoBlock>

          <InfoBlock label="Based In" inView={inView} delay={400} testid="contact-block-location">
            <p className="text-base" style={{ color: body }}>New Delhi, India</p>
          </InfoBlock>

          <InfoBlock label="Response Time" inView={inView} delay={460} testid="contact-block-response">
            <p className="text-base" style={{ color: body }}>
              Within 24–48 hours, Monday–Friday
            </p>
          </InfoBlock>
        </aside>
      </div>

      {/* Closing note */}
      <div
        className="mt-24 sm:mt-32 pt-12 border-t"
        style={{ borderColor: "rgba(26,26,25,0.12)" }}
      >
        <div
          className="max-w-[560px]"
          style={reveal(inView, 560)}
          data-testid="contact-closing"
        >
          <p
            className="font-display italic leading-[1.5]"
            style={{ fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)", color: "#1A1A19" }}
          >
            Thank you for your interest in Tamoré.
            <br />
            We look forward to hearing from you.
          </p>
        </div>
      </div>
    </main>
  );
}
